/**
 * API Key Manager Component
 * Add/remove API keys per provider with masked display
 * Validates: Requirements 12.1, 12.3, 12.5
 */
import { useState, useEffect } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { LLMProvider } from '../../types';

// Provider configuration with display info
const PROVIDERS: { id: LLMProvider; name: string; description: string; color: string }[] = [
  { id: 'openai', name: 'OpenAI', description: 'GPT-4, GPT-3.5, DALL-E', color: 'bg-green-600' },
  { id: 'anthropic', name: 'Anthropic', description: 'Claude 3.5, Claude 3', color: 'bg-orange-600' },
  { id: 'google', name: 'Google', description: 'Gemini Pro, Gemini Flash', color: 'bg-blue-600' },
  { id: 'grok', name: 'Grok (X.AI)', description: 'Grok 2, Grok 2 Mini', color: 'bg-purple-600' },
  { id: 'meta', name: 'Meta (Together AI)', description: 'LLaMA 3.1 models', color: 'bg-indigo-600' },
  { id: 'deepseek', name: 'DeepSeek', description: 'DeepSeek Chat, Coder', color: 'bg-cyan-600' },
  { id: 'qwen', name: 'Qwen (Alibaba)', description: 'Qwen Max, Plus, Turbo', color: 'bg-pink-600' },
];

interface ApiKeyModalProps {
  provider: typeof PROVIDERS[number] | null;
  onClose: () => void;
  onSubmit: (apiKey: string, validate: boolean) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function ApiKeyModal({ provider, onClose, onSubmit, isLoading, error }: ApiKeyModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [validate, setValidate] = useState(false);

  if (!provider) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    await onSubmit(apiKey.trim(), validate);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center gap-3 mb-4">
          <span className={`w-3 h-3 rounded-full ${provider.color}`} />
          <h3 className="text-lg font-semibold text-white">Add {provider.name} API Key</h3>
        </div>
        
        <p className="text-sm text-gray-400 mb-4">
          Enter your API key for {provider.name}. Your key will be encrypted and stored securely.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-300 mb-2">
              API Key
            </label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your API key..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={validate}
                onChange={(e) => setValidate(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-primary-500"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-300">Validate key with provider</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Makes a test request to verify the key is valid
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!apiKey.trim() || isLoading}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner />
                  {validate ? 'Validating...' : 'Saving...'}
                </>
              ) : (
                'Save Key'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  provider: typeof PROVIDERS[number] | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteConfirmModal({ provider, onClose, onConfirm, isLoading }: DeleteConfirmModalProps) {
  if (!provider) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-white mb-2">Delete API Key</h3>
        <p className="text-gray-400 mb-6">
          Are you sure you want to delete your {provider.name} API key? This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                Deleting...
              </>
            ) : (
              'Delete Key'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function ApiKeyManager() {
  const { 
    apiKeys, 
    apiKeysLoading, 
    apiKeysError, 
    fetchApiKeys, 
    addApiKey, 
    deleteApiKey 
  } = useSettingsStore();

  const [addModalProvider, setAddModalProvider] = useState<typeof PROVIDERS[number] | null>(null);
  const [deleteModalProvider, setDeleteModalProvider] = useState<typeof PROVIDERS[number] | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch API keys on mount
  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleAddKey = async (apiKey: string, validate: boolean) => {
    if (!addModalProvider) return;
    
    setIsSubmitting(true);
    setModalError(null);
    
    const success = await addApiKey(addModalProvider.id, apiKey, validate);
    
    setIsSubmitting(false);
    
    if (success) {
      setAddModalProvider(null);
    } else {
      setModalError(apiKeysError || 'Failed to add API key');
    }
  };

  const handleDeleteKey = async () => {
    if (!deleteModalProvider) return;
    
    setIsSubmitting(true);
    
    const success = await deleteApiKey(deleteModalProvider.id);
    
    setIsSubmitting(false);
    
    if (success) {
      setDeleteModalProvider(null);
    }
  };

  const getKeyInfo = (providerId: LLMProvider) => {
    return apiKeys.find((key) => key.provider === providerId);
  };

  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-white mb-4">API Keys</h2>
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <p className="text-gray-400 mb-6">
          Configure your API keys for different LLM providers. Your keys are encrypted and stored securely.
        </p>

        {apiKeysLoading && apiKeys.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
            <span className="ml-2 text-gray-400">Loading API keys...</span>
          </div>
        ) : (
          <div className="space-y-3">
            {PROVIDERS.map((provider) => {
              const keyInfo = getKeyInfo(provider.id);
              const isConfigured = !!keyInfo;

              return (
                <div
                  key={provider.id}
                  className="flex items-center justify-between py-4 px-4 bg-gray-800/50 rounded-lg border border-gray-700/50"
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-3 h-3 rounded-full ${provider.color}`} />
                    <div>
                      <p className="text-white font-medium">{provider.name}</p>
                      <p className="text-sm text-gray-500">{provider.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isConfigured ? (
                      <>
                        <div className="text-right mr-2">
                          <p className="text-sm text-gray-300 font-mono">{keyInfo.keyHint}</p>
                          <p className={`text-xs ${keyInfo.isValid ? 'text-green-400' : 'text-red-400'}`}>
                            {keyInfo.isValid ? 'Valid' : 'Invalid'}
                          </p>
                        </div>
                        <button
                          onClick={() => setAddModalProvider(provider)}
                          className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => setDeleteModalProvider(provider)}
                          className="px-3 py-1.5 text-sm bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setAddModalProvider(provider)}
                        className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                      >
                        Add Key
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Key Modal */}
      <ApiKeyModal
        provider={addModalProvider}
        onClose={() => {
          setAddModalProvider(null);
          setModalError(null);
        }}
        onSubmit={handleAddKey}
        isLoading={isSubmitting}
        error={modalError}
      />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        provider={deleteModalProvider}
        onClose={() => setDeleteModalProvider(null)}
        onConfirm={handleDeleteKey}
        isLoading={isSubmitting}
      />
    </section>
  );
}
